import {MatrixEvent, RelationType} from 'matrix-js-sdk';
import {buildFormattedBody, type MessageMention, type MessageRoomMention} from './messageFormatting';

export const EDIT_FALLBACK_PREFIX = ' * ';

type MinimalEvent = Pick<MatrixEvent, 'getContent'> & Partial<Pick<MatrixEvent, 'isRelation'>>;

export const isMessageEdit = (event: MinimalEvent): boolean => {
    if (typeof event.isRelation === 'function' && event.isRelation(RelationType.Replace)) {
        return true;
    }
    return event.getContent()?.['m.relates_to']?.rel_type === RelationType.Replace;
};

export const getEditTargetEventId = (event: MinimalEvent): string | null => {
    if (!isMessageEdit(event)) return null;
    const fromWire = typeof event.isRelation === 'function'
        ? (event as MatrixEvent).getRelation?.()?.event_id
        : undefined;
    return fromWire ?? event.getContent()?.['m.relates_to']?.event_id ?? null;
};

export const resolveEditTargetEventId = (event: MatrixEvent): string | null =>
    getEditTargetEventId(event) ?? event.getId() ?? null;

export interface EditableCheckOptions {
    currentUserId: string | null;
    isRedacted: boolean;
}

export const isEditableMessage = (event: MatrixEvent, {currentUserId, isRedacted}: EditableCheckOptions): boolean => {
    if (!currentUserId || event.getSender() !== currentUserId) return false;
    if (isRedacted || event.isRedacted?.()) return false;
    if (event.getType() !== 'm.room.message') return false;

    const eventId = event.getId();
    if (!eventId || eventId.startsWith('~')) return false;

    return event.getContent()?.msgtype === 'm.text';
};

export interface EditMessageContent {
    msgtype: 'm.text';
    body: string;
    format?: string;
    formatted_body?: string;
    'm.new_content': {
        msgtype: 'm.text';
        body: string;
        format?: string;
        formatted_body?: string;
        'm.mentions'?: {user_ids: string[]};
    };
    'm.relates_to': {
        rel_type: RelationType.Replace;
        event_id: string;
    };
    'm.mentions'?: {user_ids: string[]};
}

export const buildEditContent = (
    targetEventId: string,
    newText: string,
    mentions: MessageMention[] = [],
    roomMentions: MessageRoomMention[] = [],
    originalMentionedUserIds: string[] = []
): EditMessageContent => {
    const formattedBody = buildFormattedBody(newText, mentions, roomMentions);

    const newContent: EditMessageContent['m.new_content'] = {
        msgtype: 'm.text',
        body: newText,
    };
    if (formattedBody !== null) {
        newContent.format = 'org.matrix.custom.html';
        newContent.formatted_body = formattedBody;
    }
    if (mentions.length > 0) {
        newContent['m.mentions'] = {user_ids: mentions.map(m => m.userId)};
    }

    const content: EditMessageContent = {
        msgtype: 'm.text',
        body: `${EDIT_FALLBACK_PREFIX}${newText}`,
        'm.new_content': newContent,
        'm.relates_to': {
            rel_type: RelationType.Replace,
            event_id: targetEventId,
        },
    };
    if (formattedBody !== null) {
        content.format = 'org.matrix.custom.html';
        content.formatted_body = `${EDIT_FALLBACK_PREFIX}${formattedBody}`;
    }
    const newlyMentionedUserIds = mentions
        .map(mention => mention.userId)
        .filter(userId => !originalMentionedUserIds.includes(userId));
    if (newlyMentionedUserIds.length > 0) {
        content['m.mentions'] = {user_ids: newlyMentionedUserIds};
    }

    return content;
};
